// Client Portal compatibility still consumes this Preview fixture. Recovery
// Workbench requests below never send it and instead use session membership.
export const AR_RECOVERY_CANONICAL_COMPANY_ID = "company-collabrecover-preview";

export type WorkbenchResource = "notes" | "ticklers" | "promises" | "escalations";

export interface WorkbenchContextInvoice {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  originalAmountCents: number;
  openAmountCents: number;
  currency: string;
  status: string;
  agingDays: number;
  agingBucket: "CURRENT" | "1_30" | "31_60" | "61_90" | "91_PLUS";
}

export interface WorkbenchContext {
  scope: { companyId: string; clientId: string; customerId: string; accountId: string; placementId: string | null };
  client: { clientId: string; clientNumber: string; displayName: string; status: string };
  customer: { customerId: string; customerNumber: string; displayName: string; billingEmail: string | null; billingPhone: string | null; status: string };
  account: { accountId: string; accountNumber: string; status: string; placementStatus: string; currency: string; originalBalanceCents: number; currentBalanceCents: number; assignedCollectorId: string | null };
  placement: { placementId: string; placementNumber: string; placementStatus: string; placementDate: string; currentRecoveryBalanceCents: number; currency: string } | null;
  invoices: { records: WorkbenchContextInvoice[]; page: number; pageSize: number; total: number };
  recoverySummary: { notes: number; promises: number; ticklers: number; lastAction: string | null; nextAction: string | null };
}

export interface WorkbenchAccount {
  accountId: string;
  companyId: string;
  clientId: string;
  customerId: string;
  accountNumber: string;
  accountStatus: string;
  placementStatus: string;
  currency: string;
  originalBalanceCents: number;
  currentBalanceCents: number;
  assignedCollectorId: string | null;
  clientNumber: string;
  clientName: string;
  customerNumber: string;
  customerName: string;
  billingEmail: string | null;
  billingPhone: string | null;
  openInvoiceCount: number;
  oldestOpenDueDate: string | null;
  activePromiseCount: number;
  overdueTicklerCount: number;
  dueTodayTicklerCount: number;
  nearestTicklerDueAt: string | null;
  openEscalationCount: number;
  highestOpenEscalationLevel: number | null;
  latestActivityAt: string | null;
  latestActivityType: string | null;
  nextActionType: "OVERDUE_TICKLER" | "DUE_TODAY_TICKLER" | "OPEN_ESCALATION" | "ACTIVE_PROMISE" | "NO_SCHEDULED_ACTION";
  nextActionAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkbenchAccountListResult {
  records: WorkbenchAccount[];
  page: number;
  pageSize: number;
  total: number;
  totalBalanceCents: number;
}

export type WorkbenchRecord = Record<string, unknown> & {
  id: string;
  companyId: string;
  clientId: string;
  customerId: string;
  accountId: string;
  placementId?: string | null;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
};

export interface WorkbenchListResult {
  records: WorkbenchRecord[];
  page: number;
  pageSize: number;
  total: number;
}

export interface WorkbenchMutationResult {
  record: WorkbenchRecord;
  replay: boolean;
}

export async function listWorkbenchRecords(
  resource: WorkbenchResource,
  input: { accountId: string; page?: number; pageSize?: number; search?: string; status?: string; type?: string; sort?: string },
) {
  const query = new URLSearchParams({
    accountId: input.accountId,
    page: String(input.page || 1),
    pageSize: String(input.pageSize || 100),
  });
  for (const key of ["search", "status", "type", "sort"] as const) {
    if (input[key]) query.set(key, String(input[key]));
  }
  return requestJson<WorkbenchListResult>(`/api/ar-recovery/workbench/${resource}?${query}`);
}

export async function createWorkbenchRecord(resource: WorkbenchResource, input: Record<string, unknown>) {
  return requestJson<WorkbenchMutationResult>(`/api/ar-recovery/workbench/${resource}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateWorkbenchRecord(
  resource: WorkbenchResource,
  recordId: string,
  input: { expectedVersion: number; action: string; changes?: Record<string, unknown>; idempotencyKey?: string; sourceWorkspace?: string },
) {
  return requestJson<WorkbenchMutationResult>(`/api/ar-recovery/workbench/${resource}/${encodeURIComponent(recordId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function listWorkbenchTimeline(accountId: string) {
  return listPublished("timeline", accountId);
}

export async function listWorkbenchAccounts(input: { page?: number; pageSize?: number; search?: string; status?: string; sort?: "priority" | "customer" | "account" | "balance" | "updated" } = {}) {
  const query = new URLSearchParams({
    page: String(input.page || 1),
    pageSize: String(input.pageSize || 100),
  });
  for (const key of ["search", "status", "sort"] as const) {
    if (input[key]) query.set(key, String(input[key]));
  }
  return requestJson<WorkbenchAccountListResult>(`/api/ar-recovery/workbench/accounts?${query}`);
}

export async function getWorkbenchContext(accountId: string, input: { invoicePage?: number; invoicePageSize?: number } = {}) {
  const query = new URLSearchParams({
    accountId,
    invoicePage: String(input.invoicePage || 1),
    invoicePageSize: String(input.invoicePageSize || 25),
  });
  return requestJson<WorkbenchContext>(`/api/ar-recovery/workbench/context?${query}`);
}

export async function listWorkbenchActivity(accountId: string) {
  return listPublished("activity", accountId);
}

export async function importLocalWorkbenchRecords(records: Array<{ localRecordType: string; localRecordId: string; payload: Record<string, unknown> }>) {
  return requestJson<{ results: Array<{ localRecordId: string; status: string; canonicalEntityId?: string | null; reasonCode?: string | null }> }>(
    "/api/ar-recovery/workbench/local-import",
    {
      method: "POST",
      body: JSON.stringify({ records }),
    },
  );
}

async function listPublished(kind: "timeline" | "activity", accountId: string) {
  const query = new URLSearchParams({
    accountId,
    page: "1",
    pageSize: "100",
  });
  return requestJson<WorkbenchListResult>(`/api/ar-recovery/workbench/${kind}?${query}`);
}

async function requestJson<T>(url: string, init?: RequestInit) {
  const response = await fetch(url, {
    credentials: "same-origin",
    headers: init?.body ? { "content-type": "application/json", ...(init.headers || {}) } : init?.headers,
    ...init,
  });
  if (!response.ok) {
    const responseText = (await response.text()).trim();
    let message = responseText;
    try {
      const parsed = JSON.parse(responseText) as { error?: string };
      message = parsed.error || responseText;
    } catch {
      // Keep non-JSON error text from the edge runtime.
    }
    throw new Error(message || `Recovery Workbench request failed (${response.status}).`);
  }
  return response.json() as Promise<T>;
}
